// ────────────────────────────────────────────────────────────────────────────
// Pre-OCR frame-quality (motion-blur) gate flag (b13c).
//
// The b13a build added a pre-OCR motion-blur gate to useDexLiveScan
// (`evaluateFrameQuality` with `minSharpness: 0.35`). The threshold was tuned
// against SYNTHETIC crisp-checker buffers in the unit tests — never against a
// real ReplayKit broadcast JPEG (full-screen, quality 0.7, downsampled to
// 160 px before the luma-gradient measure). On device the WKWebView has
// `document` defined, so the canvas path runs and, because real broadcast
// frames score below 0.35, EVERY frame was dropped before OCR. The gate
// `return`ed before `session.ingest()`, so `framesObserved` stayed 0 and the
// PiP pill never advanced past "Open your Pokédex" (b13c bug report).
//
// This flag lets us (a) retune the blur threshold per-build without a code
// change and (b) fully bypass the blur gate as a kill-switch if it ever again
// silently swallows the pipeline.
//
// Resolution order (first wins):
//
//   1. Build-time env `VITE_DEX_BLUR_GATE`:
//        "off" / "0"      → bypass the blur gate entirely.
//        a number 0..1    → use as the minSharpness threshold.
//
//   2. localStorage["dex.scan.blurGate"]: same grammar as the env var.
//        Sticky device override via Safari Web Inspector.
//
//   3. URL hash `#dex-blur-off` → bypass for this session (promoted to LS).
//
//   4. Otherwise: gate ON at the b13c default threshold (see below). The
//      default is intentionally LOWER than b13a's 0.35 so a slightly soft but
//      readable broadcast frame is no longer dropped — blur gating exists to
//      skip frames smeared mid-scroll, not to demand pristine sharpness.
// ────────────────────────────────────────────────────────────────────────────

// b13c.4: relaxed again from b13c.3's 0.05. The build-193 device trace gated
// 120/120 live frames as motion_blur with ZERO OCR invocations — frames at
// sharpness 0.111, 0.117, even 0.256/0.265 were all dropped, which is only
// possible if the deployed floor was still ~0.35 (the b13a value), i.e. the
// 0.05 default never actually reached device. 0.03 gives maximum headroom:
// the in-hand broadcast floor on Sam's device is ~0.11, so 0.03 passes every
// real frame while still rejecting pure static / black / fully-smeared frames
// (which score ~0). To bypass the gate entirely for diagnostics, append
// `#dex-blur-off` to the URL (sticky via localStorage) or set
// localStorage["dex.scan.blurGate"]="off".
export const DEX_BLUR_GATE_DEFAULT_MIN_SHARPNESS = 0.03;

export const DEX_BLUR_GATE_LS_KEY = "dex.scan.blurGate";
export const DEX_BLUR_GATE_OFF_URL_HASH = "dex-blur-off";

export interface BlurGateConfig {
  /** When false, the pre-OCR blur gate is bypassed entirely. */
  enabled: boolean;
  /** minSharpness threshold to use when `enabled`. */
  minSharpness: number;
}

let cached: BlurGateConfig | null = null;

function parseRaw(raw: string | undefined | null): Partial<BlurGateConfig> | null {
  if (raw == null) return null;
  const v = raw.trim().toLowerCase();
  if (v === "off" || v === "0" || v === "false") return { enabled: false };
  const n = Number(v);
  if (Number.isFinite(n) && n > 0 && n <= 1) return { enabled: true, minSharpness: n };
  return null;
}

function resolve(): BlurGateConfig {
  const base: BlurGateConfig = {
    enabled: true,
    minSharpness: DEX_BLUR_GATE_DEFAULT_MIN_SHARPNESS,
  };

  // 1. Env.
  try {
    const raw = (import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }).env?.VITE_DEX_BLUR_GATE;
    const parsed = parseRaw(raw);
    if (parsed) return { ...base, ...parsed };
  } catch {
    // ignore
  }

  if (typeof window === "undefined") return base;

  // 2. localStorage.
  try {
    const parsed = parseRaw(window.localStorage.getItem(DEX_BLUR_GATE_LS_KEY));
    if (parsed) return { ...base, ...parsed };
  } catch {
    // ignore
  }

  // 3. URL hash off-probe; promote to LS so reloads stay off.
  const hash = window.location?.hash?.toLowerCase() ?? "";
  if (hash.includes(DEX_BLUR_GATE_OFF_URL_HASH)) {
    try {
      window.localStorage.setItem(DEX_BLUR_GATE_LS_KEY, "off");
    } catch {
      // ignore
    }
    return { ...base, enabled: false };
  }

  return base;
}

/**
 * Resolve the pre-OCR blur-gate configuration. Resolves at most once per
 * page-load and caches the result.
 */
export function getBlurGateConfig(): BlurGateConfig {
  if (cached !== null) return cached;
  cached = resolve();
  return cached;
}

/** Test-only escape hatch. */
export function __resetBlurGateFlagCache(): void {
  cached = null;
}
